import { utcToZonedTime, format as formatTz } from "date-fns-tz";
import { formatDistanceStrict } from "date-fns";
import { kn } from "date-fns/locale";

const TIME_ZONE = "Asia/Kolkata";

/**
 * Formats a timestamp into Prajavani story publication date & time in IST (Asia/Kolkata).
 * Uses date-fns-tz utcToZonedTime with kn locale and preserves the Kannada February correction.
 * Example: "7 ಅಕ್ಟೋಬರ್ 2026, 13:05"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} Formatted date string in Kannada, or empty string if invalid
 */
export const formatStoryDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "d MMMM yyyy, H:mm", {
    locale: kn,
    timeZone: TIME_ZONE
  }).replace("ಫೆಬ್ರವರಿ", "ಫೆಬ್ರುವರಿ");
};

/**
 * Alias for formatStoryDate for backwards compatibility across components and AMP templates.
 */
export const handleTimeFormat = (timestamp) => {
  return formatStoryDate(timestamp);
};

/**
 * Formats date only (without time) in IST in Kannada.
 * Example: "7 ಅಕ್ಟೋಬರ್ 2026"
 */
export const formatStoryDateOnly = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "d MMMM yyyy", {
    locale: kn,
    timeZone: TIME_ZONE
  }).replace("ಫೆಬ್ರವರಿ", "ಫೆಬ್ರುವರಿ");
};

/**
 * Formats date for the site logo / header in IST in Kannada.
 * Example: "ಬುಧವಾರ, 7 ಅಕ್ಟೋಬರ್ 2026"
 */
export const formatLogoDate = (timestamp = new Date()) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "eeee, d MMMM yyyy", {
    locale: kn,
    timeZone: TIME_ZONE
  }).replace("ಫೆಬ್ರವರಿ", "ಫೆಬ್ರುವರಿ");
};

/**
 * Formats timestamp to an unambiguous ISO-8601 string with explicit IST offset (+05:30).
 * Example: "2026-10-07T13:05:21+05:30"
 *
 * @param {number|string|Date} timestamp
 * @returns {string} ISO-8601 timestamp string with +05:30 offset
 */
export const toISTIsoString = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "yyyy-MM-dd'T'HH:mm:ssXXX", {
    timeZone: TIME_ZONE
  });
};

/**
 * Converts timestamp to "YYYY-MM-DD HH:mm" in IST for DataLayer analytics.
 * Example: "2026-10-07 13:05"
 */
export const convertTimestampToYYYYMMDDHHMM = (timestamp) => {
  if (!timestamp) return "NA";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "NA";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "yyyy-MM-dd HH:mm", {
    timeZone: TIME_ZONE
  });
};

/**
 * Formats time in IST for live blog cards: "HH:mm" (e.g. "13:05").
 */
export const formatLiveBlogTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "HH:mm", {
    timeZone: TIME_ZONE
  });
};

/**
 * Formats date in IST for live blog cards: "dd MMM yyyy" (e.g. "07 Oct 2026").
 */
export const formatLiveBlogDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const zonedDate = utcToZonedTime(date, TIME_ZONE);
  return formatTz(zonedDate, "dd MMM yyyy", {
    timeZone: TIME_ZONE
  });
};

/**
 * Extracts the canonical publication/update timestamp from a story object or raw timestamp.
 * Canonical precedence: last-published-at -> published-at -> first-published-at
 *
 * @param {object|number|string|Date} storyOrTimestamp
 * @returns {number|string|Date|null}
 */
export const getStoryTimestamp = (storyOrTimestamp) => {
  if (!storyOrTimestamp) return null;
  if (
    typeof storyOrTimestamp === "number" ||
    typeof storyOrTimestamp === "string" ||
    storyOrTimestamp instanceof Date
  ) {
    return storyOrTimestamp;
  }
  return (
    storyOrTimestamp["last-published-at"] ||
    storyOrTimestamp["published-at"] ||
    storyOrTimestamp["first-published-at"] ||
    null
  );
};

/**
 * Computes structured relative time information for a given timestamp.
 * Standardized across all story cards and listings using round-to-nearest policy.
 *
 * @param {number|string|Date} timestamp
 * @param {object} [options]
 * @param {Date|number} [options.baseDate] - Reference base date (defaults to current time)
 * @returns {object|null}
 */
export const getRelativeTimeInfo = (timestamp, options = {}) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return null;

  const baseDate = options.baseDate ? new Date(options.baseDate) : new Date();
  if (isNaN(baseDate.getTime())) return null;

  const diffMs = Math.max(0, baseDate.getTime() - date.getTime());
  const hours = diffMs / (1000 * 60 * 60);

  // Stories 24 hours or older switch to absolute IST date formatting
  if (hours >= 24) {
    const formattedStoryDate = formatStoryDate(date);
    const formattedDateOnly = formatStoryDateOnly(date);
    return {
      isOlderThan24Hours: true,
      isJustNow: false,
      count: null,
      unit: "day",
      desktopUnit: "",
      mobileUnit: "",
      desktopUnitKn: "",
      mobileUnitKn: "",
      formattedStoryDate,
      formattedDateOnly,
      fullText: `${formattedStoryDate} IST`
    };
  }

  // Under 60 seconds
  if (diffMs < 60 * 1000) {
    return {
      isOlderThan24Hours: false,
      isJustNow: true,
      count: null,
      unit: "minute",
      desktopUnit: "Just now",
      mobileUnit: "Just now",
      desktopUnitKn: "Just now",
      mobileUnitKn: "Just now",
      fullText: "Just now",
      formattedStoryDate: formatStoryDate(date),
      formattedDateOnly: formatStoryDateOnly(date)
    };
  }

  const timeDistance = formatDistanceStrict(date, baseDate, { roundingMethod: "round" });
  const [count, rawUnit] = timeDistance.split(" ");
  const isHour = rawUnit.startsWith("hour");

  const desktopUnit = isHour
    ? count === "1"
      ? "hour ago"
      : "hours ago"
    : count === "1"
    ? "minute ago"
    : "minutes ago";

  const mobileUnit = isHour ? (count === "1" ? "hr. ago" : "hrs. ago") : count === "1" ? "min. ago" : "mins. ago";

  // Kannada localization for Prajavani
  const desktopUnitKn = isHour ? "ಗಂಟೆ ಹಿಂದೆ" : "ನಿಮಿಷ ಹಿಂದೆ";
  const mobileUnitKn = isHour ? "ಗಂ. ಹಿಂದೆ" : "ನಿ. ಹಿಂದೆ";

  return {
    isOlderThan24Hours: false,
    isJustNow: false,
    count,
    unit: isHour ? "hour" : "minute",
    desktopUnit,
    mobileUnit,
    desktopUnitKn,
    mobileUnitKn,
    fullText: `${count} ${desktopUnit}`,
    formattedStoryDate: formatStoryDate(date),
    formattedDateOnly: formatStoryDateOnly(date)
  };
};

/**
 * Returns formatted relative or absolute date string for story cards.
 * If < 24h: returns relative time (e.g. "4 hours ago", "1 hour ago", "Just now").
 * If >= 24h: returns absolute IST date (e.g. "7 ಅಕ್ಟೋಬರ್ 2026, 12:10 IST").
 *
 * @param {object|number|string|Date} storyOrTimestamp
 * @param {object} [options]
 * @returns {string}
 */
export const formatRelativeTime = (storyOrTimestamp, options = {}) => {
  const timestamp = getStoryTimestamp(storyOrTimestamp);
  if (!timestamp) return "";
  const info = getRelativeTimeInfo(timestamp, options);
  if (!info) return "";
  return info.fullText;
};
